import React, { useEffect, useRef, useState } from 'react';
import { filterLanguages, getLanguageById } from '../utils/languages';

const LineNumbersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 6H6M5 12H7M5 18H8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 6H20M12 12H20M12 18H20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const WrapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M3 6H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M3 12H18C19.6569 12 21 13.3431 21 15C21 16.6569 19.6569 18 18 18H14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M16 16L14 18L16 20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M3 18H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

const CopiedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 13L9 17L19 7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CodeBlockHeader = ({
  language,
  wrapText,
  showLineNumbers,
  copied,
  onLanguageSelect,
  onToggleWrap,
  onToggleLineNumbers,
  onCopy,
}) => {
  const menuRef = useRef(null);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');

  useEffect(() => {
    if (!showLanguageMenu) return undefined;
    const onDocMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowLanguageMenu(false);
        setLanguageSearch('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showLanguageMenu]);

  const filteredLanguages = filterLanguages(languageSearch);
  const currentLanguage = getLanguageById(language);

  const handleLanguageSelect = (langId) => {
    onLanguageSelect(langId);
    setShowLanguageMenu(false);
    setLanguageSearch('');
  };

  return (
    <div className="code-block-header">
      <div className="code-block-language" ref={menuRef}>
        <button
          type="button"
          className="code-block-language-btn"
          onClick={(e) => {
            e.stopPropagation();
            setShowLanguageMenu((v) => !v);
          }}
        >
          {currentLanguage.label}
          <span className="code-block-language-arrow">▾</span>
        </button>

        {showLanguageMenu && (
          <div className="code-block-language-menu">
            <input
              type="text"
              className="code-block-language-search"
              placeholder="Search language…"
              value={languageSearch}
              onChange={(e) => setLanguageSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <div className="code-block-language-list">
              {filteredLanguages.map((lang) => (
                <button
                  key={lang.id}
                  type="button"
                  className={`code-block-language-option ${lang.id === language ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageSelect(lang.id);
                  }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="code-block-actions">
        <button
          type="button"
          className={`code-block-action ${showLineNumbers ? 'active' : ''}`}
          onClick={onToggleLineNumbers}
          title={showLineNumbers ? 'Hide line numbers' : 'Show line numbers'}
        >
          <LineNumbersIcon />
        </button>
        <button
          type="button"
          className={`code-block-action ${wrapText ? 'active' : ''}`}
          onClick={onToggleWrap}
          title={wrapText ? 'Disable word wrap' : 'Enable word wrap'}
        >
          <WrapIcon />
        </button>
        <button
          type="button"
          className={`code-block-action ${copied ? 'active' : ''}`}
          onClick={onCopy}
          title="Copy code"
        >
          {copied ? <CopiedIcon /> : <CopyIcon />}
        </button>
      </div>
    </div>
  );
};
